/** node@+18.x.x versions with TS do not support fetch global api yet.
 * Also, the native Fetch API is currently not implemented consistently across browsers.
 * Solution: https://github.com/matthew-andrews/isomorphic-fetch
 */

import fetch from 'isomorphic-fetch';
import { url } from '@ts/utils/url';

type SecretLinkDto = {
  id: string;
  article: string;
  user_email: string;
  use_limit: number;
  use_count: number;
  created_at: string;
  expired_at: string;
};

type IncreaseUseLimitDto = {
  secret_link_id: string;
  increase_value: number;
};

type UpdateExpiryDateDto = {
  secret_link_id: string;
  new_expiry_date: string;
};

export type SecretLinkUrlDto =
  | {
      status: 'success' | 'created';
      url: string;
    }
  | {
      status: 'error';
      message: string;
    };

type RemainingSecretLinksCountDto = {
  count: number;
  has_valid_link: boolean;
};

export class SecretLinksService {
  constructor(private readonly baseUrl: string = url.getBaseUrl()) {}

  increaseUseLimit = async (
    secret_link_id: string,
    increase_value: number,
  ): Promise<SecretLinkDto> => {
    const url = `${this.baseUrl}/@@secret-links-api/increase_use_limit`;

    const dto: IncreaseUseLimitDto = {
      secret_link_id,
      increase_value,
    };

    const settings: RequestInit = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dto),
      credentials: 'include',
    };

    const response = await fetch(url, settings);
    if (!response.ok) {
      const msg = await response.text();
      throw new Error(`Status code:${response.status}\nError:${msg}`);
    }

    const data: SecretLinkDto = await response.json();

    return data;
  };

  updateExpiryDate = async (
    secretLinkId: string,
    newExpiryDate: string,
  ): Promise<SecretLinkDto> => {
    const url = `${this.baseUrl}/@@secret-links-api/update_expiry_date`;

    const dto: UpdateExpiryDateDto = {
      secret_link_id: secretLinkId,
      new_expiry_date: newExpiryDate,
    };

    const settings: RequestInit = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(dto),
      credentials: 'include',
    };

    const response = await fetch(url, settings);
    if (!response.ok) {
      const msg = await response.text();
      throw new Error(`Status code:${response.status}\nError:${msg}`);
    }

    const data: SecretLinkDto = await response.json();

    return data;
  };

  getSecretLinkUrl = async (): Promise<SecretLinkUrlDto> => {
    const url = `${this.baseUrl}/@@secret-links-api/get_subscriber_secret_link`;

    const settings: RequestInit = {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    };

    const response = await fetch(url, settings);

    if (!response.ok) {
      const status = 'error';
      const message = await response.text();

      return { status, message };
    }

    const status = response.status === 201 ? 'created' : 'success';
    const res = await response.json();

    return { status, url: res.url };
  };

  getRemainingSecretLinksCount =
    async (): Promise<RemainingSecretLinksCountDto> => {
      const url = `${this.baseUrl}/@@secret-links-api/get_remaining_secret_links_count`;

      const settings: RequestInit = {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      };

      const response = await fetch(url, settings);
      if (!response.ok) {
        const msg = await response.text();
        throw new Error(`Status code:${response.status}\nError:${msg}`);
      }

      const data: RemainingSecretLinksCountDto = await response.json();

      return data;
    };
}
